import type { CSSProperties } from 'react';
import type { ClientRoomState, SpotifyAuthStatus } from '@hitster/shared';
import { playerColor } from '@hitster/shared';
import { useCoverColor } from '../hooks/useCoverColor.ts';
import { useT, type Translate } from '../i18n.tsx';
import type { TranslationKey } from '../locales.ts';
import { Alert, Countdown, Tokens } from '../components/common.tsx';
import { QrJoin } from '../components/QrJoin.tsx';
import { Deck } from '../components/Deck.tsx';
import { RoomMenu } from '../components/RoomMenu.tsx';
import { SpeakerMenu } from '../components/SpeakerMenu.tsx';
import { RevealCard } from '../components/RevealCard.tsx';
import { PlayerList } from '../components/PlayerList.tsx';
import { Podium } from '../components/Podium.tsx';
import { Timeline } from '../components/Timeline.tsx';
import { PhaseTimer } from '../components/PhaseTimer.tsx';
import { Turntable } from '../components/Turntable.tsx';
import type { LogEntry, RoomApi } from '../net/useRoom.ts';

/**
 * TV-/Beamer-Ansicht.
 *
 * Bewusst ohne Bedienelemente ausser dem Weiter-Knopf: gesteuert wird
 * per Handy. Alles ist groesser dimensioniert, damit man es vom Sofa
 * aus lesen kann.
 */
export function ScreenPage({
  state,
  api,
  log,
  authStatus,
}: {
  state: ClientRoomState;
  api: RoomApi;
  log: LogEntry[];
  authStatus: SpotifyAuthStatus | null;
}) {
  const t = useT();

  const active = state.players.find((player) => player.id === state.activePlayerId) ?? null;
  const isHost = state.hostId === state.youId;

  // Am grossen Bildschirm ist die Raumfaerbung der eigentliche Moment.
  const revealed = state.phase === 'reveal' || state.phase === 'finished';
  const coverColor = useCoverColor(revealed ? state.lastResult?.card.coverUrl : null);

  return (
    <div
      className={`page screen-view ${revealed && coverColor ? 'page--lit' : ''}`}
      style={coverColor ? ({ '--cover': coverColor } as CSSProperties) : undefined}
    >
      {/* ---------------- Kopf ---------------- */}
      <div className="row row--between">
        <div className="row">
          <span className="badge">{state.code}</span>
          <Deck remaining={state.deckRemaining} />
          <span className="text-dim text-sm">
            {t('audio.count', { count: state.audioDeviceCount })}
          </span>
        </div>
        <div className="row" style={{ gap: '0.25rem' }}>
          <Countdown deadline={state.phaseDeadline} />
          {/* Auch am grossen Bildschirm muss man Ton und Lautstaerke
              erreichen, ohne zur Steuerung zurueckzuwechseln. */}
          <SpeakerMenu state={state} api={api} authStatus={authStatus} />
          <RoomMenu state={state} api={api} />
        </div>
      </div>

      {state.youAreSpectator && <Alert kind="info">{t('spectator.youAre')}</Alert>}

      {/* ---------------- Hauptbereich ---------------- */}
      {state.phase === 'reveal' || state.phase === 'finished' ? (
        <div className="grid-2">
          <div className="stack">
            {/*
             * Am Spielende tritt das Podium an die Stelle der
             * Aufdeck-Karte. Sonst bliebe der letzte Song das grosse
             * Bild — und der interessiert dann niemanden mehr.
             */}
            {state.phase !== 'finished' && state.lastResult && (
              <RevealCard result={state.lastResult} state={state} large />
            )}

            {state.phase === 'finished' && (
              <div className="panel stack">
                <h1 className="text-center">{t('finished.title')}</h1>
                <h2 className="text-center">
                  {t(
                    state.winnerIds.length === 1 ? 'finished.winner' : 'finished.winners',
                    {
                      // Der Singular-Text erwartet `name`, der Plural `names`.
                      name:
                        state.players.find((p) => p.id === state.winnerIds[0])?.name ?? '—',
                      names: state.winnerIds
                        .map((id) => state.players.find((p) => p.id === id)?.name ?? '—')
                        .join(', '),
                    },
                  )}
                </h2>

                <Podium state={state} />

                {isHost && (
                  <button type="button" className="btn btn--primary btn--lg" onClick={api.restartGame}>
                    {t('finished.backToLobby')}
                  </button>
                )}
              </div>
            )}

            {state.phase === 'reveal' && isHost && !state.settings.autoAdvance && (
              <button type="button" className="btn btn--primary btn--lg btn--block" onClick={api.advance}>
                {t('reveal.next')}
              </button>
            )}
          </div>

          <ScreenSidebar state={state} log={log} t={t} />
        </div>
      ) : (
        <div className="grid-2">
          <div className="stack">
            <h1>{t('game.turnOf', { name: active?.name ?? '—' })}</h1>

            {/* Auf drei Metern Abstand stand hier bisher nur ein grauer
                Absatz. Die Platte traegt die Phase. */}
            {state.phase === 'playing' && (
              <>
                <Turntable large />
                <p className="text-muted">{t('game.listening')}</p>
              </>
            )}
            {state.phase === 'stealing' && (
              <div className="stack stack--tight">
                <p style={{ color: 'var(--warning)', fontWeight: 700, margin: 0 }}>
                  {t('steal.window')}
                </p>
                <PhaseTimer
                  deadline={state.phaseDeadline}
                  totalMs={state.settings.stealWindowSec * 1000}
                  label={t('steal.timeLeft')}
                />
              </div>
            )}

            {active && (
              <div className="panel">
                <div className="panel__title">
                  {active.name} ·{' '}
                  {t('game.cards', {
                    count: active.timeline.length,
                    target: state.settings.targetCards,
                  })}
                </div>
                <Timeline
                  cards={active.timeline}
                  ownerId={active.id}
                  // Schon waehrend des Ueberlegens sichtbar, nicht erst
                  // im Klau-Fenster -- auf dem Fernseher ist das der
                  // Moment, dem alle zusehen.
                  selectedSlot={state.activePlacement?.slotIndex ?? null}
                  live={state.phase === 'playing'}
                />
              </div>
            )}

            {state.phase === 'stealing' && state.stealClaims.length > 0 && (
              <div className="panel">
                <div className="panel__title">{t('steal.window')}</div>
                {state.stealClaims.map((claim) => (
                  <div key={claim.playerId}>
                    {t('steal.someoneClaimed', {
                      name: state.players.find((p) => p.id === claim.playerId)?.name ?? '—',
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          <ScreenSidebar state={state} log={log} t={t} />
        </div>
      )}

      {/* ---------------- Alle Zeitleisten ---------------- */}
      <div className="stack stack--tight">
        {state.players
          .filter((player) => player.id !== state.activePlayerId)
          .map((player) => (
            <div
              key={player.id}
              className="panel panel--player"
              style={{ padding: '0.75rem', '--player': playerColor(player.id) } as CSSProperties}
            >
              <div className="row row--between">
                <span className="row" style={{ gap: '0.5rem', fontWeight: 700 }}>
                  <span className="player-row__dot" aria-hidden="true" />
                  {player.name}
                </span>
                <div className="row" style={{ gap: '0.5rem' }}>
                  {state.settings.maxTokens > 0 && (
                    <Tokens count={player.tokens} max={state.settings.maxTokens} />
                  )}
                  <span className="text-sm text-dim">
                    {t('game.cards', {
                      count: player.timeline.length,
                      target: state.settings.targetCards,
                    })}
                  </span>
                </div>
              </div>
              <Timeline cards={player.timeline} ownerId={player.id} compact />
            </div>
          ))}
      </div>
    </div>
  );
}

/** Rechte Spalte der TV-Ansicht: Beitritt, Punkte, Ereignis-Log. */
function ScreenSidebar({
  state,
  log,
  t,
}: {
  state: ClientRoomState;
  log: LogEntry[];
  t: Translate;
}) {
  return (
    <div className="stack">
      <div className="panel">
        <QrJoin joinUrl={state.joinUrl} code={state.code} />
      </div>

      {/*
       * Punktestand — der fehlte auf dem Fernseher komplett.
       *
       * Darunter stand ein Panel mit dem Titel „Spieler", das in
       * Wahrheit das Ereignis-Log enthielt. Wer wie viele Karten hat,
       * war auf dem grossen Bildschirm nirgends abzulesen.
       */}
      <div className="panel">
        <div className="panel__title">{t('lobby.players')}</div>
        <PlayerList state={state} />
      </div>

      <div className="panel">
        <div className="panel__title">{t('screen.log')}</div>
        <div className="log">
          {log.map((entry) => (
            <div key={entry.id} className="log__entry">
              {t(entry.key as TranslationKey, entry.params)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
